import React, { useState, useEffect } from "react";

// Import Firebase config
import { db } from './firebase'; 
import { getDoc, doc, setDoc } from 'firebase/firestore';

function DailylimitSwipesCount() {
  const [swipeCount, setSwipeCount] = useState(null);
  const [profileViewCount, setProfileViewCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSwipe, setEditingSwipe] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state for editing
  const [editSwipeValue, setEditSwipeValue] = useState('');
  const [editProfileValue, setEditProfileValue] = useState('');

  useEffect(() => {
    fetchCurrentLimits();
  }, []);

  const fetchCurrentLimits = async () => {
    try {
      setLoading(true);
      
      // Fetch only the RegisteredUsers document to get the current limits
      const registeredUsersRef = doc(db, 'users', 'RegisteredUsers');
      const docSnapshot = await getDoc(registeredUsersRef);
      
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        console.log('RegisteredUsers data:', userData);
        
        // Extract daily limits from the correct structure
        const swipeCount = userData.dailyLimit?.swipeCount;
        const profileViewCount = userData.dailyLimit?.profileViewCount;
        
        setSwipeCount(typeof swipeCount === 'number' ? swipeCount : 0);
        setProfileViewCount(typeof profileViewCount === 'number' ? profileViewCount : 0);
      } else {
        // Initialize with default values if document doesn't exist
        setSwipeCount(0);
        setProfileViewCount(0);
      }
      
    } catch (err) {
      console.error('Error fetching daily limits:', err);
      setError('Failed to load daily limits');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSwipe = () => {
    setEditingSwipe(true);
    setEditSwipeValue(swipeCount.toString());
  };

  const handleEditProfile = () => {
    setEditingProfile(true);
    setEditProfileValue(profileViewCount.toString());
  };

  const handleSaveSwipe = async () => {
    if (!editSwipeValue) {
      alert('Please enter a value');
      return;
    }

    if (isNaN(editSwipeValue) || Number(editSwipeValue) < 0) {
      alert('Please enter a valid non-negative number');
      return;
    }

    try {
      setSaving(true);
      
      // Update only the RegisteredUsers document
      const registeredUsersRef = doc(db, 'users', 'RegisteredUsers');
      
      // First get the current document to preserve existing data
      const docSnapshot = await getDoc(registeredUsersRef);
      let currentData = {};
      if (docSnapshot.exists()) {
        currentData = docSnapshot.data();
      }
      
      // Update only the swipeCount, preserving profileViewCount
      await setDoc(registeredUsersRef, {
        ...currentData,
        dailyLimit: {
          ...currentData.dailyLimit,
          swipeCount: Number(editSwipeValue)
        }
      }, { merge: true });
      
      setSwipeCount(Number(editSwipeValue));
      setEditingSwipe(false);
      setEditSwipeValue('');
      alert(`Swipe count updated successfully!`);
      
    } catch (err) {
      console.error('Error updating swipe count:', err);
      alert('Failed to update swipe count. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!editProfileValue) {
      alert('Please enter a value');
      return;
    }

    if (isNaN(editProfileValue) || Number(editProfileValue) < 0) {
      alert('Please enter a valid non-negative number');
      return;
    }

    try {
      setSaving(true);
      
      // Update only the RegisteredUsers document
      const registeredUsersRef = doc(db, 'users', 'RegisteredUsers');
      
      // First get the current document to preserve existing data
      const docSnapshot = await getDoc(registeredUsersRef);
      let currentData = {};
      if (docSnapshot.exists()) {
        currentData = docSnapshot.data();
      }
      
      // Update only the profileViewCount, preserving swipeCount
      await setDoc(registeredUsersRef, {
        ...currentData,
        dailyLimit: {
          ...currentData.dailyLimit,
          profileViewCount: Number(editProfileValue)
        }
      }, { merge: true });
      
      setProfileViewCount(Number(editProfileValue));
      setEditingProfile(false);
      setEditProfileValue('');
      alert(`Profile view count updated successfully!`);
      
    } catch (err) {
      console.error('Error updating profile view count:', err);
      alert('Failed to update profile view count. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSwipe = () => {
    setEditingSwipe(false);
    setEditSwipeValue('');
  };

  const handleCancelProfile = () => {
    setEditingProfile(false);
    setEditProfileValue('');
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#4a90e2' }}></i>
          <p>Loading daily limits...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#e74c3c' }}></i>
          <p>{error}</p>
          <button onClick={fetchCurrentLimits} style={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>
        <i className="fas fa-chart-line" style={{ marginRight: "10px", color: "#FF027D" }}></i>
        Daily Limit
      </h2>

      <div style={styles.cardsContainer}>
        {/* Swipe Count Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <i 
              className="fas fa-hand-pointer" 
              style={styles.icon}
            ></i>
          </div>

          <h3 style={styles.cardTitle}>Daily Swipe Limit</h3>
          <p style={styles.description}>
            This limit applies to all users
          </p>

          {editingSwipe ? (
            // Edit Mode
            <div style={styles.editSection}>
              <div style={styles.editField}>
                <label style={styles.label}>Swipe Count:</label>
                <input
                  type="number"
                  value={editSwipeValue}
                  onChange={(e) => setEditSwipeValue(e.target.value)}
                  style={styles.input}
                  min="0"
                  disabled={saving}
                  placeholder="Enter swipe count"
                />
              </div>

              <div style={styles.editActions}>
                <button 
                  onClick={handleSaveSwipe}
                  style={styles.saveButton}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }}></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check" style={{ marginRight: '5px' }}></i>
                      Save
                    </>
                  )}
                </button>
                <button 
                  onClick={handleCancelSwipe}
                  style={styles.cancelButton}
                  disabled={saving}
                >
                  <i className="fas fa-times" style={{ marginRight: '5px' }}></i>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // View Mode
            <div style={styles.viewSection}>
              <div style={styles.countDisplay}>
                <span style={styles.countLabel}>Current Limit:</span>
                <span style={styles.countValue}>{swipeCount}</span>
                <span style={styles.countUnit}>swipes/day</span>
              </div>
              
              <button 
                onClick={handleEditSwipe}
                style={styles.editButton}
                disabled={editingProfile}
              >
                <i className="fas fa-edit" style={{ marginRight: '5px' }}></i>
                Edit Limit
              </button>
            </div>
          )}
        </div>

        {/* Profile View Count Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <i 
              className="fas fa-eye" 
              style={{...styles.icon, color: "#667eea"}}
            ></i>
          </div>

          <h3 style={styles.cardTitle}>Daily Profile View Limit</h3>
          <p style={styles.description}>
            This limit applies to all users
          </p>

          {editingProfile ? (
            // Edit Mode
            <div style={styles.editSection}>
              <div style={styles.editField}>
                <label style={styles.label}>Profile View Count:</label>
                <input
                  type="number"
                  value={editProfileValue}
                  onChange={(e) => setEditProfileValue(e.target.value)}
                  style={styles.input}
                  min="0"
                  disabled={saving}
                  placeholder="Enter profile view count"
                />
              </div>

              <div style={styles.editActions}>
                <button 
                  onClick={handleSaveProfile}
                  style={styles.saveButton}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }}></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check" style={{ marginRight: '5px' }}></i>
                      Save
                    </>
                  )}
                </button>
                <button 
                  onClick={handleCancelProfile}
                  style={styles.cancelButton}
                  disabled={saving}
                >
                  <i className="fas fa-times" style={{ marginRight: '5px' }}></i>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // View Mode
            <div style={styles.viewSection}>
              <div style={styles.countDisplay}>
                <span style={styles.countLabel}>Current Limit:</span>
                <span style={{...styles.countValue, color: "#667eea"}}>{profileViewCount}</span>
                <span style={styles.countUnit}>views/day</span>
              </div>
              
              <button 
                onClick={handleEditProfile}
                style={{...styles.editButton, backgroundColor: "#667eea"}}
                disabled={editingSwipe}
              >
                <i className="fas fa-edit" style={{ marginRight: '5px' }}></i>
                Edit Limit
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: "40px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    textAlign: "center",
    minHeight: "100vh",
  },
  heading: {
    fontSize: "2.5rem",
    marginBottom: "40px",
    color: "#ee4782",
    fontWeight: "600",
  },
  cardsContainer: {
    display: "flex",
    gap: "30px",
    justifyContent: "center",
    flexWrap: "wrap",
    maxWidth: "900px",
    margin: "0 auto",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "40px 30px",
    width: "401px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    border: "2px solid transparent",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  icon: {
    fontSize: "3rem",
    color: "#FF027D",
  },
  cardTitle: {
    fontSize: "1.8rem",
    margin: "10px 0 15px 0",
    color: "#2c3e50",
    fontWeight: "600",
  },
  description: {
    color: "#7f8c8d",
    fontSize: "0.95rem",
    marginBottom: "25px",
  },
  viewSection: {
    display: "flex",
    flexDirection: "column",
    gap: "25px",
  },
  countDisplay: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "25px",
    backgroundColor: "#f8f9fc",
    borderRadius: "12px",
    border: "2px solid #e3f2fd",
  },
  countLabel: {
    fontSize: "1rem",
    color: "#7f8c8d",
    fontWeight: "500",
  },
  countValue: {
    fontSize: "3.5rem",
    fontWeight: "700",
    color: "#FF027D",
  },
  countUnit: {
    fontSize: "1rem",
    color: "#95a5a6",
    fontWeight: "500",
  },
  editSection: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    textAlign: "left",
    backgroundColor: "#f8f9fc",
    padding: "25px",
    borderRadius: "12px",
    border: "2px solid #e3f2fd",
  },
  editField: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  label: {
    fontSize: "1rem",
    fontWeight: "600",
    color: "#34495e",
  },
  input: {
    padding: "14px",
    border: "2px solid #e1e8ed",
    borderRadius: "8px",
    fontSize: "1.1rem",
    transition: "border-color 0.3s ease",
    backgroundColor: "white",
  },
  editActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    marginTop: "10px",
  },
  editButton: {
    backgroundColor: "#ee4782",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "12px 30px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  saveButton: {
    backgroundColor: "#27ae60",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "12px 25px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    flex: 1,
  },
  cancelButton: {
    backgroundColor: "#95a5a6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "12px 25px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    flex: 1,
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    gap: "20px",
  },
  error: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    gap: "20px",
    color: "#e74c3c",
  },
  retryButton: {
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    cursor: "pointer",
    fontSize: "1rem",
  },
};

export default DailylimitSwipesCount;